import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import PMStatus from './PMStatus';
import PMProgress from './PMProgress';
import PMList from './PMList';
import { HiOutlineDownload, HiOutlineDocumentText, HiOutlineViewList, HiArrowsExpand, HiX, HiZoomIn, HiZoomOut, HiOutlineAdjustments } from 'react-icons/hi';

const PMTable = ({ children, className = '', ...props }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);
  const inlineDropdownRef = React.useRef(null);
  const modalDropdownRef = React.useRef(null);
  const [columns, setColumns] = React.useState([]);
  const [visibleColumns, setVisibleColumns] = React.useState(new Set());
  const [isInlineColumnSelectorOpen, setIsInlineColumnSelectorOpen] = React.useState(false);
  const [isModalColumnSelectorOpen, setIsModalColumnSelectorOpen] = React.useState(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoom(1);
  // Ensure children is always a string
  const content = React.useMemo(() => {
    if (typeof children === 'string') {
      return children;
    }
    if (Array.isArray(children)) {
      return children
        .filter(child => typeof child === 'string' || typeof child === 'number')
        .join('');
    }
    if (children != null) {
      return String(children);
    }
    return '';
  }, [children]);

  // Function to sanitize PM tags from text for export
  const sanitizePMTags = (text) => {
    if (!text || typeof text !== 'string') return '';
    // Remove PM tags but keep their content
    return text.replace(/<pm:(status|progress|list)(?:\s+[^>]*?)?>(.*?)<\/pm:\1>/gs, '$2').trim();
  };

  // Parse table data for export
  const parseTableForExport = React.useCallback(() => {
    const lines = content.trim().split('\n').filter(line => line.trim());
    if (lines.length < 3) return { headers: [], rows: [] };
    
    // Parse headers - split by | and process each cell, keeping empty ones
    const headerCells = lines[0].split('|');
    const headers = headerCells.map(h => sanitizePMTags(h.trim()));
    // Remove first and last elements if they are empty (markdown table format)
    if (headers.length > 0 && headers[0] === '') headers.shift();
    if (headers.length > 0 && headers[headers.length - 1] === '') headers.pop();
    
    // Parse data rows - maintain column count based on headers
    const dataRows = lines.slice(2).map(line => {
      const cells = line.split('|');
      let rowData = cells.map(cell => sanitizePMTags(cell.trim()));
      
      // Remove first and last elements if they are empty (markdown table format)
      if (rowData.length > 0 && rowData[0] === '') rowData.shift();
      if (rowData.length > 0 && rowData[rowData.length - 1] === '') rowData.pop();
      
      // Ensure row has same number of columns as headers
      while (rowData.length < headers.length) {
        rowData.push(''); // Add empty cells if row is shorter
      }
      
      // Trim row if it's longer than headers
      if (rowData.length > headers.length) {
        rowData = rowData.slice(0, headers.length);
      }
      
      return rowData;
    });
    
    return { headers, rows: dataRows };
  }, [content]);

  React.useEffect(() => {
    const { headers } = parseTableForExport();
    setColumns(headers);
    setVisibleColumns(new Set(headers));
  }, [parseTableForExport]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (inlineDropdownRef.current && !inlineDropdownRef.current.contains(event.target)) {
        setIsInlineColumnSelectorOpen(false);
      }
      if (modalDropdownRef.current && !modalDropdownRef.current.contains(event.target)) {
        setIsModalColumnSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleColumn = (columnName) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnName)) {
        // Don't allow hiding if it's the last visible column
        if (newSet.size > 1) {
          newSet.delete(columnName);
        }
      } else {
        newSet.add(columnName);
      }
      return newSet;
    });
  };

  const visibleColumnIndices = React.useMemo(() => 
    columns.map((col, i) => visibleColumns.has(col) ? i : -1).filter(i => i !== -1),
    [columns, visibleColumns]
  );

  // Don't render if content is empty
  if (!content.trim()) {
    return (
      <div className="pm-table-placeholder border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-gray-400 text-sm">
        No table data available
      </div>
    );
  }

  // Export to Excel
  const exportToExcel = () => {
    const { headers, rows } = parseTableForExport();
    if (headers.length === 0) return;
    const visibleHeaders = headers.filter((_, index) => visibleColumnIndices.includes(index));
    const visibleRows = rows.map(row => row.filter((_, index) => visibleColumnIndices.includes(index)));
    const csvContent = [visibleHeaders.join(','), ...visibleRows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'table_export.csv';
    link.click();
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const { headers, rows } = parseTableForExport();
    if (headers.length === 0) return;
    const visibleHeaders = headers.filter((_, index) => visibleColumnIndices.includes(index));
    const visibleRows = rows.map(row => row.filter((_, index) => visibleColumnIndices.includes(index)));
    const textContent = [visibleHeaders.join('\t'), ...visibleRows.map(row => row.join('\t'))].join('\n');
    navigator.clipboard.writeText(textContent);
  };

  // Function to process PM tags in table cells
  const preprocessTableContent = (content) => {
    if (!content || typeof content !== 'string') return { processedContent: '', pmTagMap: new Map() };
    
    const pmTagMap = new Map();
    let tagCounter = 0;
    
    const regex = /<pm:(status|progress|list)(?:\s+([^>]*?))?>(.*?)<\/pm:\1>/gs;

    let processedContent = content.replace(regex, (match, tagName, attributes, innerContent) => {
      const placeholder = `PMTAGPLACEHOLDER${tagCounter}`;

      const attrs = {};
      if (attributes) {
        const attrRegex = /(\w+)="([^"]*?)"/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(attributes)) !== null) {
          attrs[attrMatch[1]] = attrMatch[2];
        }
      }

      pmTagMap.set(placeholder, {
        tag: tagName,
        attrs: attrs,
        content: innerContent
      });
      tagCounter++;
      return placeholder;
    });

    return { processedContent, pmTagMap };
  };

  // Custom components with improved colors
  const CustomTable = ({ children, hideHeader = false, maxHeight, ...props }) => (
    <div className="pm-table-wrapper w-full rounded-xl border border-gray-100 dark:border-gray-700/20 shadow-sm bg-white dark:bg-gray-900/30 backdrop-blur-sm">
      {/* Export Controls - merged with table */}
      {!hideHeader && (
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700/20 rounded-t-xl">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
            <HiOutlineViewList className="h-4 w-4" />
            Table
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
              title="View Full Screen"
            >
              <HiArrowsExpand className="h-4 w-4" />
            </button>
            <div className="relative" ref={inlineDropdownRef}>
              <button
                onClick={() => setIsInlineColumnSelectorOpen(prev => !prev)}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
                title="Select columns"
              >
                <HiOutlineAdjustments className="h-4 w-4" />
              </button>
              {isInlineColumnSelectorOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20">
                  <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                    <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 font-semibold border-b border-gray-200 dark:border-gray-700">
                      Show/Hide Columns
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {columns.map((columnName) => (
                        <label key={columnName} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-blue-600 dark:text-blue-500 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                            checked={visibleColumns.has(columnName)}
                            onChange={() => toggleColumn(columnName)}
                          />
                          <span className="ml-3 truncate" title={columnName}>{columnName}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button 
              onClick={copyToClipboard} 
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
              title="Copy table data"
            >
              <HiOutlineDocumentText className="h-4 w-4" />
            </button>
            <button 
              onClick={exportToExcel} 
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
              title="Export to CSV"
            >
              <HiOutlineDownload className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {/* Table - directly connected */}
      <div className={`overflow-x-auto overflow-y-auto ${maxHeight ? '' : 'max-h-96'} border-collapse [&_*]:text-gray-800 [&_*]:dark:text-gray-200 rounded-b-xl [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a:hover]:underline`} style={maxHeight ? { maxHeight } : undefined}>
        <table className="w-full border-collapse" {...props}>{children}</table>
      </div>
    </div>
  );

  const CustomTableHead = ({ children, ...props }) => (
    <thead 
      className="sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/20" 
      {...props}
    >
      {children}
    </thead>
  );

  const CustomTableBody = ({ children, ...props }) => (
    <tbody className="bg-white dark:bg-gray-900/30 divide-y divide-gray-100 dark:divide-gray-700/10" {...props}>{children}</tbody>
  );

  const CustomTableRow = ({ children, ...props }) => {
    const filteredChildren = React.useMemo(() => 
      React.Children.toArray(children).filter((_, index) => visibleColumnIndices.includes(index)),
      [children, visibleColumnIndices]
    );
    return (
      <tr className="bg-white dark:bg-tertiary-dark hover:bg-gray-50 dark:hover:bg-[#1f29374a] transition-colors duration-150 ease-in-out" {...props}>
        {filteredChildren}
      </tr>
    );
  };

  const CustomTableHeader = ({ children, ...props }) => (
    <th 
      className="sticky top-0 px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider z-10
        bg-white dark:bg-tertiary-dark border-r !border-t-0 !border-b border-[#f3f4f6] dark:border-gray-700/20 last:border-r-0 relative" 
      {...props}
    >
      <div className="truncate [&_*]:text-gray-600 [&_*]:dark:text-gray-300 [&_strong]:font-bold [&_strong]:text-gray-700 [&_strong]:dark:text-gray-200 [&_mark]:bg-yellow-200 [&_mark]:dark:bg-yellow-800 [&_mark]:px-1 [&_mark]:rounded">{children}</div>
    </th>
  );

  const CustomTableCell = ({ children, pmTagMap, ...props }) => {
    const processedChildren = React.Children.map(children, child => {
      if (typeof child === 'string' && pmTagMap) {
        const parts = child.split(/(PMTAGPLACEHOLDER\d+)/);
        return parts.map((part, index) => {
          if (pmTagMap.has(part)) {
            const tagInfo = pmTagMap.get(part);
            const key = `pm-tag-${index}`;
            switch (tagInfo.tag) {
              case 'status':
                return <PMStatus key={key} type={tagInfo.attrs.type}>{tagInfo.content}</PMStatus>;
              case 'progress':
                return <PMProgress key={key} value={tagInfo.attrs.value} />;
              case 'list':
                return <PMList key={key} {...tagInfo.attrs}>{tagInfo.content}</PMList>;
              default:
                return part;
            }
          }
          return part;
        });
      }
      return child;
    });

    return (
      <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200" {...props}>
        <div className="break-words [&_*]:text-gray-800 [&_*]:dark:text-gray-200 [&_strong]:font-bold [&_strong]:text-gray-900 [&_strong]:dark:text-gray-100 [&_mark]:bg-yellow-200 [&_mark]:dark:bg-yellow-800 [&_mark]:px-1 [&_mark]:rounded [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a:hover]:underline">{processedChildren}</div>
      </td>
    );
  };

  const { processedContent, pmTagMap } = preprocessTableContent(content);
  const TableCellWithMap = (props) => <CustomTableCell {...props} pmTagMap={pmTagMap} />;

  const CustomMark = ({ children, ...props }) => (
    <mark className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded text-gray-900 dark:text-gray-100" {...props}>
      {children}
    </mark>
  );

  return (
    <div className={`pm-table ${className}`} {...props}>
      <ReactMarkdown
        components={{
          table: CustomTable,
          thead: CustomTableHead,
          tbody: CustomTableBody,
          tr: CustomTableRow,
          th: CustomTableHeader,
          td: TableCellWithMap,
          mark: CustomMark,
        }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {processedContent}
      </ReactMarkdown>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative max-w-full max-h-full p-4 w-full h-full">
            <div className="absolute top-4 left-4 right-4 z-10 bg-black bg-opacity-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white truncate">Table</h3>
                <div className="flex items-center gap-2">
                  <button onClick={handleZoomOut} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Zoom out">
                    <HiZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-white text-sm px-2">{Math.round(zoom * 100)}%</span>
                  <button onClick={handleZoomIn} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Zoom in">
                    <HiZoomIn className="w-4 h-4" />
                  </button>
                  <button onClick={handleResetZoom} className="text-white text-sm px-2 hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Reset zoom">
                    Reset
                  </button>
                  <div className="relative" ref={modalDropdownRef}>
                    <button
                      onClick={() => setIsModalColumnSelectorOpen(prev => !prev)}
                      className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all"
                      title="Select columns"
                    >
                      <HiOutlineAdjustments className="w-4 h-4" />
                    </button>
                    {isModalColumnSelectorOpen && (
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-20">
                        <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                          <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 font-semibold border-b border-gray-200 dark:border-gray-700">
                            Show/Hide Columns
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {columns.map((columnName) => (
                              <label key={columnName} className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-blue-600 dark:text-blue-500 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                                  checked={visibleColumns.has(columnName)}
                                  onChange={() => toggleColumn(columnName)}
                                />
                                <span className="ml-3 truncate" title={columnName}>{columnName}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={copyToClipboard} 
                    className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all"
                    title="Copy table data"
                  >
                    <HiOutlineDocumentText className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={exportToExcel} 
                    className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all"
                    title="Export to CSV"
                  >
                    <HiOutlineDownload className="w-4 h-4" />
                  </button>
                  <button onClick={() => setIsModalOpen(false)} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all">
                    <HiX className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-16 max-w-full max-h-full overflow-auto flex items-start justify-center [&_*]:text-gray-800 [&_*]:dark:text-gray-200" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
              {/* Expanded Table View */}
              <div className="w-[85%] mx-auto" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
                  <ReactMarkdown
                    components={{
                      table: ({ children: modalChildren, ...modalProps }) => (
                        <CustomTable hideHeader maxHeight={'calc(100vh - 6rem)'} {...modalProps}>{modalChildren}</CustomTable>
                      ),
                      thead: CustomTableHead,
                      tbody: CustomTableBody,
                      tr: CustomTableRow,
                      th: CustomTableHeader,
                      td: TableCellWithMap,
                      mark: CustomMark,
                    }}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {processedContent}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PMTable;